/**
 * Poseidon dashboard — REST client.
 *
 * Contract notes for the Rust API owner:
 *
 * - Every response is an `ApiEnvelope<T>`: `{ data, as_of, server_time,
 *   provenance, warnings }`. `as_of` is what drives the dashboard's staleness
 *   indicator, so it must be the time the *data* was computed, not the time the
 *   response was serialized.
 * - Authentication is a SameSite cookie set by `POST /api/auth/login`. The
 *   browser sends it automatically; this client sets `credentials:
 *   'same-origin'` so a same-origin deployment works without CORS.
 * - State-changing requests carry `x-poseidon-csrf` matching the token from
 *   `GET /api/auth/session` (§16.4.1). The token is held in memory only — never
 *   localStorage/sessionStorage, never logged.
 * - Command endpoints must independently re-verify the typed confirmation
 *   phrase, the operator's authority, and current risk state. The dialog in this
 *   UI is an operator safeguard, not a security boundary.
 */
import type {
  AllowListEntryView,
  ApiEnvelope,
  ApiErrorBody,
  AuthSessionView,
  BacktestRunView,
  BarView,
  BridgeStatusView,
  ChartSeries,
  ClosedTradeRow,
  CommandReceipt,
  CommandRequest,
  DataHealthView,
  DataSourceMode,
  FeatureFreshnessRow,
  IndicatorSeriesView,
  KpiSnapshot,
  Paged,
  PositionRow,
  QuoteRowView,
  ReadinessView,
  RegimeSnapshotView,
  ResearchAssessmentView,
  ResearchCallCostSummary,
  RibbonStatus,
  RiskConfigAuditRow,
  RiskLimitRow,
  SchedulerView,
  SignalBreakdownView,
  SignalRow,
  StrategyEligibilityView,
  StrategyMarkerView,
  SystemEventRow,
  TerminalMetadataView,
  Timeframe,
  TradeDecisionTrailView,
  TradeFilters,
  WorkingOrderRow,
} from './types';
import { API_BASE_URL } from '../config';

/** CSRF header name; must match the daemon's extractor. */
export const CSRF_HEADER = 'x-poseidon-csrf';

/** A REST payload plus the provenance needed to judge whether to trust it. */
export interface Fetched<T> {
  data: T;
  /** When the daemon computed this payload. Drives the staleness indicator. */
  asOf: string;
  serverTime: string;
  provenance: 'live' | 'replay' | 'synthetic';
  warnings: string[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: { field: string; message: string }[];
  readonly correlationId: string | null;
  readonly retryable: boolean;

  constructor(status: number, body: Partial<ApiErrorBody> | null, fallbackMessage: string) {
    super(body?.message ?? fallbackMessage);
    this.name = 'ApiError';
    this.status = status;
    this.code = body?.code ?? `http_${status}`;
    this.fields = body?.fields ?? [];
    this.correlationId = body?.correlation_id ?? null;
    this.retryable = body?.retryable ?? (status >= 500 || status === 429);
  }

  /** Operator-facing text. Redacted by construction: no request bodies are included. */
  get displayMessage(): string {
    if (this.status === 0) return 'The local daemon is not reachable on this origin.';
    if (this.status === 401) return 'The operator session has expired. Sign in again to continue.';
    if (this.status === 403) return 'The daemon refused this action for this session.';
    if (this.status === 409) return `The daemon rejected this action: ${this.message}`;
    if (this.status === 422) return `The daemon rejected the request as invalid: ${this.message}`;
    return this.message;
  }
}

/* ------------------------------------------------------------------ *
 * In-memory CSRF token
 * ------------------------------------------------------------------ */

let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Registered by the auth provider so a 401 can drop the operator back to login. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** RFC 4122 v4 id for command idempotency keys. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for non-secure contexts. Loopback is a secure context, so this is
  // only a belt-and-braces path; it is not cryptographically strong, but the
  // daemon's idempotency guard is what actually prevents duplicates.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/* ------------------------------------------------------------------ *
 * Core request
 * ------------------------------------------------------------------ */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Send the CSRF header. Required for every state-changing request. */
  csrf?: boolean;
  signal?: AbortSignal;
  /** Query parameters; null/undefined entries are dropped. */
  query?: Record<string, string | number | boolean | null | undefined | string[]>;
  accept?: string;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) params.append(key, String(item));
    } else {
      params.append(key, String(value));
    }
  }
  const qs = params.toString();
  return qs.length > 0 ? `${url}?${qs}` : url;
}

async function rawRequest(path: string, options: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = { Accept: options.accept ?? 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (options.csrf) {
    if (csrfToken === null) {
      // Fail before touching the network: without a token the daemon will
      // reject the command anyway, and the operator needs to know why.
      throw new ApiError(403, { code: 'csrf_token_missing', message: 'No CSRF token is loaded for this session.', retryable: false }, 'CSRF token missing');
    }
    headers[CSRF_HEADER] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      credentials: 'same-origin',
      cache: 'no-store',
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    // Network-level failure. Status 0 keeps it distinguishable from an HTTP error.
    throw new ApiError(0, { code: 'network_unreachable', message: 'Could not reach the local daemon.', retryable: true }, 'network unreachable');
  }

  if (response.status === 401) {
    unauthorizedHandler?.();
  }

  if (!response.ok) {
    let body: Partial<ApiErrorBody> | null = null;
    try {
      body = (await response.json()) as Partial<ApiErrorBody>;
    } catch {
      body = null;
    }
    throw new ApiError(response.status, body, `Request to ${path} failed with status ${response.status}`);
  }
  return response;
}

function unwrap<T>(envelope: ApiEnvelope<T>): Fetched<T> {
  return {
    data: envelope.data,
    asOf: envelope.as_of,
    serverTime: envelope.server_time,
    provenance: envelope.provenance,
    warnings: envelope.warnings ?? [],
  };
}

async function getJson<T>(path: string, options: RequestOptions = {}): Promise<Fetched<T>> {
  const response = await rawRequest(path, { ...options, method: 'GET' });
  const envelope = (await response.json()) as ApiEnvelope<T>;
  return unwrap(envelope);
}

async function postJson<T>(path: string, body: unknown): Promise<Fetched<T>> {
  const response = await rawRequest(path, { method: 'POST', body, csrf: true });
  const envelope = (await response.json()) as ApiEnvelope<T>;
  return unwrap(envelope);
}

/**
 * The dashboard's entire view of the daemon. Both the live client and the mock
 * client implement this interface, so switching between them is a single build
 * flag and no page code changes.
 */
export interface DashboardApi {
  readonly mode: DataSourceMode;

  /* auth (§3.1) */
  getSession(): Promise<Fetched<AuthSessionView>>;
  login(operator: string, password: string): Promise<Fetched<AuthSessionView>>;
  logout(): Promise<Fetched<{ ok: boolean }>>;

  /* §3.2 A */
  getRibbon(): Promise<Fetched<RibbonStatus>>;
  getKpis(sessionDate?: string): Promise<Fetched<KpiSnapshot>>;
  getPositions(): Promise<Fetched<PositionRow[]>>;
  getWorkingOrders(): Promise<Fetched<WorkingOrderRow[]>>;
  getClosedTrades(filters: TradeFilters, cursor?: string): Promise<Fetched<Paged<ClosedTradeRow>>>;
  getSignals(): Promise<Fetched<SignalRow[]>>;
  getSystemEvents(since?: string): Promise<Fetched<SystemEventRow[]>>;

  /* §3.2 B */
  getDecisionTrail(completedTradeId: string): Promise<Fetched<TradeDecisionTrailView>>;
  getChart(chartId: ChartSeries['chart_id'], filters: TradeFilters): Promise<Fetched<ChartSeries>>;

  /* §3.2 C */
  getQuotes(symbols?: string[]): Promise<Fetched<QuoteRowView[]>>;
  getBars(symbol: string, timeframe: Timeframe, limit: number): Promise<Fetched<BarView[]>>;
  getIndicators(symbol: string, timeframe: Timeframe): Promise<Fetched<IndicatorSeriesView[]>>;
  getMarkers(symbol: string): Promise<Fetched<StrategyMarkerView[]>>;
  getSignalBreakdown(decisionId: string): Promise<Fetched<SignalBreakdownView>>;

  /* §3.2 D */
  getRiskLimits(): Promise<Fetched<RiskLimitRow[]>>;
  getRiskAuditLog(): Promise<Fetched<RiskConfigAuditRow[]>>;
  getAllowLists(): Promise<Fetched<AllowListEntryView[]>>;

  /* §3.2 E */
  getStrategies(): Promise<Fetched<StrategyEligibilityView[]>>;
  getResearchAssessments(limit?: number): Promise<Fetched<ResearchAssessmentView[]>>;
  getRegimes(symbol?: string): Promise<Fetched<RegimeSnapshotView[]>>;
  getFeatureFreshness(): Promise<Fetched<FeatureFreshnessRow[]>>;
  getResearchCosts(): Promise<Fetched<ResearchCallCostSummary>>;

  /* §3.2 F */
  getBacktests(): Promise<Fetched<BacktestRunView[]>>;
  getBacktest(runId: string): Promise<Fetched<BacktestRunView>>;

  /* §3.2 G */
  getReadiness(): Promise<Fetched<ReadinessView>>;
  getBridgeStatus(): Promise<Fetched<BridgeStatusView>>;
  getTerminalMetadata(): Promise<Fetched<TerminalMetadataView>>;
  getScheduler(): Promise<Fetched<SchedulerView>>;
  getDataHealth(): Promise<Fetched<DataHealthView>>;

  /* §3.1 commands — all require an explicit typed confirmation */
  sendCommand<TPayload>(request: CommandRequest<TPayload>): Promise<Fetched<CommandReceipt>>;
}

/** How each command maps onto a route. Exported so the daemon contract is explicit. */
export const COMMAND_ROUTES = {
  set_execution_mode: '/commands/execution-mode',
  approve_order_intent: '/commands/order-intents/approve',
  reject_order_intent: '/commands/order-intents/reject',
  cancel_order: '/commands/orders/cancel',
  close_position: '/commands/positions/close',
  set_halt: '/commands/halt',
  set_risk_limit: '/commands/risk-limits',
  set_allow_list: '/commands/allow-lists',
} as const;

/* ------------------------------------------------------------------ *
 * Live client
 * ------------------------------------------------------------------ */

function tradeFilterQuery(filters: TradeFilters): Record<string, string | string[] | null> {
  return {
    from: filters.from,
    to: filters.to,
    sessions: filters.sessions,
    strategies: filters.strategies,
    symbols: filters.symbols,
    outcomes: filters.outcomes,
    exit_reasons: filters.exit_reasons,
  };
}

export function createLiveApi(): DashboardApi {
  return {
    mode: 'live',

    getSession: () => getJson<AuthSessionView>('/auth/session'),
    login: (operator, password) => postJson<AuthSessionView>('/auth/login', { operator, password }),
    logout: () => postJson<{ ok: boolean }>('/auth/logout', {}),

    getRibbon: () => getJson<RibbonStatus>('/status/ribbon'),
    getKpis: (sessionDate) => getJson<KpiSnapshot>('/overview/kpis', { query: { session_date: sessionDate } }),
    getPositions: () => getJson<PositionRow[]>('/positions'),
    getWorkingOrders: () => getJson<WorkingOrderRow[]>('/orders/working'),
    getClosedTrades: (filters, cursor) =>
      getJson<Paged<ClosedTradeRow>>('/trades/closed', { query: { ...tradeFilterQuery(filters), cursor } }),
    getSignals: () => getJson<SignalRow[]>('/signals'),
    getSystemEvents: (since) => getJson<SystemEventRow[]>('/events/system', { query: { since } }),

    getDecisionTrail: (id) => getJson<TradeDecisionTrailView>(`/trades/closed/${encodeURIComponent(id)}/decision-trail`),
    getChart: (chartId, filters) => getJson<ChartSeries>(`/analytics/charts/${chartId}`, { query: tradeFilterQuery(filters) }),

    getQuotes: (symbols) => getJson<QuoteRowView[]>('/market/quotes', { query: { symbols } }),
    getBars: (symbol, timeframe, limit) => getJson<BarView[]>('/market/bars', { query: { symbol, timeframe, limit } }),
    getIndicators: (symbol, timeframe) => getJson<IndicatorSeriesView[]>('/market/indicators', { query: { symbol, timeframe } }),
    getMarkers: (symbol) => getJson<StrategyMarkerView[]>('/market/markers', { query: { symbol } }),
    getSignalBreakdown: (decisionId) => getJson<SignalBreakdownView>(`/signals/${encodeURIComponent(decisionId)}/breakdown`),

    getRiskLimits: () => getJson<RiskLimitRow[]>('/risk/limits'),
    getRiskAuditLog: () => getJson<RiskConfigAuditRow[]>('/risk/audit-log'),
    getAllowLists: () => getJson<AllowListEntryView[]>('/risk/allow-lists'),

    getStrategies: () => getJson<StrategyEligibilityView[]>('/research/strategies'),
    getResearchAssessments: (limit) => getJson<ResearchAssessmentView[]>('/research/assessments', { query: { limit } }),
    getRegimes: (symbol) => getJson<RegimeSnapshotView[]>('/research/regimes', { query: { symbol } }),
    getFeatureFreshness: () => getJson<FeatureFreshnessRow[]>('/research/feature-freshness'),
    getResearchCosts: () => getJson<ResearchCallCostSummary>('/research/costs'),

    getBacktests: () => getJson<BacktestRunView[]>('/backtests'),
    getBacktest: (runId) => getJson<BacktestRunView>(`/backtests/${encodeURIComponent(runId)}`),

    getReadiness: () => getJson<ReadinessView>('/readiness'),
    getBridgeStatus: () => getJson<BridgeStatusView>('/operations/bridge'),
    getTerminalMetadata: () => getJson<TerminalMetadataView>('/operations/terminal'),
    getScheduler: () => getJson<SchedulerView>('/operations/scheduler'),
    getDataHealth: () => getJson<DataHealthView>('/operations/data-health'),

    sendCommand: <TPayload>(request: CommandRequest<TPayload>) =>
      postJson<CommandReceipt>(COMMAND_ROUTES[request.command], request),
  };
}
